<template>
  <section
    class="comparison-panel"
    :class="{ 'shared-period': mode === 'distribution' && !reason }"
    :aria-label="l('Flere fag i samme graf', 'Multiple courses in one chart')"
  >
    <header class="comparison-heading">
      <div>
        <h2>{{ title }}</h2>
      </div>
      <span class="comparison-count"
        >{{ chartData.datasets.length }} / {{ items.length }}
        {{ l("fag synlige", "courses visible") }}</span
      >
    </header>
    <div
      v-if="!reason"
      class="comparison-legend"
      :aria-label="l('Velg synlige fag', 'Choose visible courses')"
    >
      <label
        v-for="entry in summaries"
        :key="entry.item.course.id"
        class="comparison-legend-item"
        :class="{ 'is-hidden': hiddenIds.includes(entry.item.course.id) }"
        :title="entry.item.course.course_name"
      >
        <input
          type="checkbox"
          :checked="!hiddenIds.includes(entry.item.course.id)"
          :disabled="!seriesFor(entry.item.course.id)"
          :aria-label="`${l('Vis', 'Show')} ${entry.item.course.course_name}`"
          @change="toggle(entry.item.course.id)"
        />
        <svg class="legend-line" viewBox="0 0 24 8" aria-hidden="true">
          <line
            x1="0"
            y1="4"
            x2="24"
            y2="4"
            :stroke="seriesFor(entry.item.course.id)?.borderColor || '#8a9682'"
            :stroke-dasharray="
              seriesFor(entry.item.course.id)?.borderDash?.join(' ')
            "
            stroke-width="3"
          />
        </svg>
        <span>{{ entry.item.course.course_code }}</span>
      </label>
    </div>
    <p
      v-if="mode === 'distribution' && smallSamples.length"
      class="basis-note sample-alert"
      role="status"
    >
      {{
        l("Lite utvalg i valgt periode", "Small sample in the selected period")
      }}:
      {{
        smallSamples
          .map(
            (entry) =>
              `${entry.item.course.course_code} (n=${format(entry.count, 0)})`
          )
          .join(", ")
      }}.
    </p>
    <p v-if="excludedCourses.length" class="basis-note" role="status">
      {{
        l(
          "Ingen grafdata i denne visningen for",
          "No chart data in this view for"
        )
      }}: {{ excludedCourses.join(", ") }}.
    </p>
    <p v-if="womenEnabled" class="basis-note">
      {{
        l(
          "Felles graf viser alle vurderinger. Kvinneutvalget kan utforskes i fagkortene.",
          "The shared chart shows all results. Explore the women’s subset in course cards."
        )
      }}
    </p>
    <div v-if="reason" class="comparison-empty" role="status">
      <span aria-hidden="true">↔</span>
      <h3>{{ reasonTitle }}</h3>
      <p>{{ reasonText }}</p>
      <div class="comparison-recovery">
        <button
          v-if="reason === 'different-periods' && canAlign"
          type="button"
          @click="$emit('align')"
        >
          {{
            l("Bruk siste felles periode", "Use latest shared period")
          }}</button
        ><button
          v-if="reason !== 'ambiguous-periods'"
          type="button"
          @click="
            $emit(
              'mode',
              reason === 'different-scales' ? 'failRates' : 'historical'
            )
          "
        >
          {{
            reason === "different-scales"
              ? l("Sammenlign stryk", "Compare failure rates")
              : l("Vis utvikling over tid", "Show trends over time")
          }}</button
        ><button type="button" @click="$emit('cards')">
          {{ l("Vis fagkort", "Show course cards") }}
        </button>
      </div>
    </div>
    <ChartComponent
      v-else-if="chartData.datasets.length"
      :chart-data="plotData"
      :chart-options="options"
      :chart-type="mode === 'distribution' ? 'bar' : 'line'"
      :accessible-label="`${title}. ${description} ${l(
        'Bruk talltabellen nedenfor for alle verdier.',
        'Use the data table below for all values.'
      )}`"
      :error-label="
        l(
          'Grafen kunne ikke vises. Åpne talltabellen nedenfor.',
          'Unable to display the chart. Open the data table below.'
        )
      "
    />
    <div v-else class="comparison-empty" role="status">
      <p>
        {{
          l(
            "Velg minst ett fag i forklaringen over for å vise grafen.",
            "Select at least one course in the legend above to display the chart."
          )
        }}
      </p>
      <button type="button" class="show-all-courses" @click="hiddenIds = []">
        {{ l("Vis alle fag", "Show all courses") }}
      </button>
    </div>
    <div class="comparison-footnote">
      <span>{{
        mode === "distribution"
          ? l(
              "Prosentene bruker hvert fags registrerte karakterer som grunnlag. Se utvalgsvarsler og nøkkeltall nedenfor.",
              "Percentages use each course’s recorded grades as their denominator. See sample warnings and key figures below."
            )
          : l(
              "▲ = færre enn 50 vurderinger i punktets beregningsgrunnlag. Linjer binder registrerte punkter; det beregnes ingen verdi for perioder uten registrering.",
              "▲ = fewer than 50 results underpinning that point. Lines join observed points; no value is calculated for periods without an observation."
            )
      }}</span
      ><button
        v-if="!reason"
        type="button"
        :aria-expanded="showTable"
        aria-controls="combined-data-table"
        @click="showTable = !showTable"
      >
        {{
          showTable
            ? l("Skjul talltabell", "Hide data table")
            : l("Vis talltabell", "View data table")
        }}
        {{ showTable ? "−" : "+" }}
      </button>
    </div>
    <div
      v-if="showTable && !reason"
      id="combined-data-table"
      class="comparison-table-wrap"
      tabindex="0"
      :aria-label="
        l('Tall for grafen, rull ved behov', 'Chart data, scroll if needed')
      "
    >
      <table>
        <caption>
          {{
            title
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {{
                mode === "distribution"
                  ? l("Karakter", "Grade")
                  : l("Periode", "Period")
              }}
            </th>
            <th
              v-for="dataset in chartData.datasets"
              :key="dataset.courseId"
              scope="col"
            >
              {{ dataset.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, i) in chartData.labels" :key="i">
            <th scope="row">{{ label }}</th>
            <td v-for="dataset in chartData.datasets" :key="dataset.courseId">
              {{ tableValue(dataset, i) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="comparison-summary-wrap">
      <table class="comparison-summary">
        <caption>
          {{
            mode === "distribution"
              ? l(
                  "Nøkkeltall for valgt periode",
                  "Key figures for the selected period"
                )
              : l(
                  "Nøkkeltall fra siste registrerte periode per fag",
                  "Key figures from each course’s latest recorded period"
                )
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col">{{ l("Fag", "Course") }}</th>
            <th scope="col">{{ l("Periode", "Period") }}</th>
            <th scope="col">{{ l("Snitt / 5", "Average / 5") }}</th>
            <th scope="col">{{ l("Stryk", "Failure") }}</th>
            <th scope="col">{{ l("Grafgrunnlag", "Chart sample") }}</th>
            <th scope="col">
              <span class="sr-only">{{ l("Fjern fag", "Remove course") }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in summaries"
            :key="entry.item.course.id"
            :class="{ 'is-hidden': hiddenIds.includes(entry.item.course.id) }"
          >
            <th scope="row" class="comparison-course">
              <span class="comparison-course-label">
                <svg class="legend-line" viewBox="0 0 24 8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="24"
                    y2="4"
                    :stroke="
                      seriesFor(entry.item.course.id)?.borderColor || '#8a9682'
                    "
                    :stroke-dasharray="
                      seriesFor(entry.item.course.id)?.borderDash?.join(' ')
                    "
                    stroke-width="3"
                  />
                </svg>
                <span
                  ><strong>{{ entry.item.course.course_code }}</strong>
                  <span class="comparison-course-name">{{
                    entry.item.course.course_name
                  }}</span>
                </span>
              </span>
            </th>
            <td :data-label="l('Periode', 'Period')">{{ entry.period }}</td>
            <td
              :data-label="l('Snitt / 5', 'Average / 5')"
              class="summary-value"
            >
              {{ format(entry.summary.average, 2) }}
            </td>
            <td :data-label="l('Stryk', 'Failure')" class="summary-value">
              {{
                entry.summary.failRate == null
                  ? "—"
                  : `${format(entry.summary.failRate * 100, 1)} %`
              }}
            </td>
            <td :data-label="l('Grafgrunnlag', 'Chart sample')">
              <SampleSizeBadge
                :count="entry.count"
                :locale="locale"
                always
                compact
              /><span
                v-if="entry.count !== entry.summary.count"
                class="summary-total"
                >{{ l("Alle resultater", "All results") }}:
                {{ format(entry.summary.count, 0) }}</span
              >
            </td>
            <td class="comparison-remove-cell">
              <button
                type="button"
                class="comparison-remove"
                :aria-label="`${l('Fjern', 'Remove')} ${
                  entry.item.course.course_name
                }`"
                @click="$emit('remove', entry.item.course.id)"
              >
                ×
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="basis-note">
      {{
        excludeFails
          ? l(
              "Snittet utelater F. Strykandelen inkluderer alle vurderinger. n gjelder grafens beregningsgrunnlag.",
              "The average excludes F. Failure rate covers all results. n refers to the chart’s calculation sample."
            )
          : l(
              "Snittet inkluderer F. Strykandelen inkluderer alle vurderinger. n gjelder grafens beregningsgrunnlag.",
              "The average includes F. Failure rate covers all results. n refers to the chart’s calculation sample."
            )
      }}
    </p>
  </section>
</template>

<script>
import ChartComponent from "./ChartComponent.vue";
import SampleSizeBadge from "./SampleSizeBadge.vue";
import {
  combineCourseSeries,
  summaryForRow,
  metricSampleSize,
  sortTerms,
  termKey,
  numberOrNull,
  uniquePeriodRow,
  comparisonPlotData,
} from "../utils/gradeStatistics.mjs";
export default {
  name: "CourseComparison",
  components: { ChartComponent, SampleSizeBadge },
  props: {
    items: { type: Array, default: () => [] },
    mode: { type: String, default: "distribution" },
    chartOptions: { type: Object, required: true },
    locale: { type: String, default: "no" },
    excludeFails: Boolean,
    womenEnabled: Boolean,
    canAlign: Boolean,
  },
  emits: ["remove", "mode", "align", "cards"],
  data: () => ({ hiddenIds: [], showTable: false }),
  computed: {
    smallSamples() {
      return this.summaries.filter(
        (entry) => entry.count != null && entry.count < 50
      );
    },
    plotData() {
      return comparisonPlotData(this.chartData, this.items, this.mode);
    },
    excludedCourses() {
      return this.items
        .filter(
          (item) =>
            !item.chartData.datasets[0]?.data.some(
              (value) => numberOrNull(value) != null
            )
        )
        .map((item) => item.course.course_code);
    },
    model() {
      return combineCourseSeries(this.items, this.mode);
    },
    chartData() {
      return {
        labels: this.model.labels,
        datasets: this.model.datasets.filter(
          (dataset) => !this.hiddenIds.includes(dataset.courseId)
        ),
      };
    },
    reason() {
      return this.model.reason;
    },
    title() {
      return this.mode === "distribution"
        ? this.l("Karakterfordeling", "Grade distribution")
        : this.mode === "historical"
        ? this.l("Karaktersnitt over tid", "Average grades over time")
        : this.l("Strykandel over tid", "Failure rates over time");
    },
    description() {
      return this.mode === "distribution"
        ? this.l(
            "Grupperte søyler viser prosentfordeling i samme periode. Trykk på grafen for antall, eller åpne talltabellen.",
            "Grouped bars show percentage distributions in the same period. Tap the chart for counts, or open the data table."
          )
        : this.l(
            "Én linje per fag, med felles tidsakse og skala. Velg hvilke fag som vises i oversikten over.",
            "One line per course on a shared timeline and scale. Choose visible courses in the overview above."
          );
    },
    reasonTitle() {
      if (this.reason === "ambiguous-periods")
        return this.l(
          "Flere delutvalg i samme periode.",
          "Multiple populations in the same period."
        );
      return this.reason === "different-periods"
        ? this.l("Velg samme periode først.", "Choose a shared period first.")
        : this.reason === "different-scales"
        ? this.l(
            "Disse fagene har ulike karakterskalaer.",
            "These courses have different grading scales."
          )
        : this.l(
            "Ikke nok sammenlignbare data.",
            "Not enough comparable data."
          );
    },
    reasonText() {
      if (this.reason === "ambiguous-periods")
        return this.l(
          "Disse registreringene kan ikke skilles entydig i denne visningen. Avgrens til lærested/studieprogram før du sammenligner.",
          "These observations cannot be uniquely distinguished in this view. Restrict the institution/programme before comparing."
        );
      return this.reason === "different-periods"
        ? this.l(
            "Bruk periodevalget over. Fordelinger fra ulike perioder legges ikke i samme graf.",
            "Use the period selector above. Distributions from different periods are not combined."
          )
        : this.reason === "different-scales"
        ? this.l(
            "A–F og bestått/ikke bestått kan ikke dele karakterakse. Prøv strykandeler, eller bruk fagkortene.",
            "A–F and pass/fail cannot share a grade axis. Try failure rates, or use course cards."
          )
        : this.l(
            "Noen fag mangler tall for denne visningen. Prøv en annen periode eller strykvisningen.",
            "Some courses have no figures for this view. Try another period or the failure-rate view."
          );
    },
    summaries() {
      return this.items.map((item) => {
        const rows = sortTerms(item.rows);
        const row =
          this.mode === "distribution"
            ? uniquePeriodRow(rows, item.course.selectedTermKey)
            : rows.length
            ? uniquePeriodRow(rows, termKey(rows[rows.length - 1]))
            : undefined;
        return {
          item,
          summary: summaryForRow(row, this.excludeFails),
          count: metricSampleSize(row, this.mode, this.excludeFails),
          period: row
            ? item.terms.find((term) => term.key === termKey(row))?.label ||
              row.year
            : "—",
        };
      });
    },
    options() {
      const periods = this.items[0]?.chartData.periods || [];
      const xValues = periods.map(
        (period) => Number(period.year) * 4 + Number(period.semester)
      );
      const linearX =
        this.mode !== "distribution" && xValues.length
          ? {
              x: {
                type: "linear",
                min: Math.floor(Math.min(...xValues) / 4) * 4,
                max: Math.max(...xValues),
                grid: { display: false },
                ticks: {
                  maxTicksLimit: 6,
                  maxRotation: 0,
                  stepSize: 4,
                  includeBounds: false,
                  color: "#6b7863",
                  callback: (value) => {
                    return Number.isInteger(value / 4) ? String(value / 4) : "";
                  },
                },
              },
            }
          : {};
      return {
        ...this.chartOptions,
        scales: { ...this.chartOptions.scales, ...linearX },
        interaction: { mode: "nearest", axis: "xy", intersect: false },
        plugins: {
          ...this.chartOptions.plugins,
          legend: { display: false },
          tooltip: {
            ...this.chartOptions.plugins?.tooltip,
            mode: "nearest",
            callbacks: {
              title: (contexts) =>
                contexts[0]?.raw?.periodLabel || contexts[0]?.label || "",
              label: (context) => this.tooltipValue(context),
            },
          },
        },
      };
    },
  },
  watch: {
    items() {
      this.hiddenIds = this.hiddenIds.filter((id) =>
        this.items.some((item) => item.course.id === id)
      );
    },
  },
  methods: {
    l(no, en) {
      return this.locale === "no" ? no : en;
    },
    format(value, decimals = 1) {
      return value == null
        ? "—"
        : new Intl.NumberFormat(this.locale === "no" ? "nb-NO" : "en-GB", {
            maximumFractionDigits: decimals,
          }).format(value);
    },
    seriesFor(id) {
      return this.model.datasets.find((dataset) => dataset.courseId === id);
    },
    toggle(id) {
      this.hiddenIds = this.hiddenIds.includes(id)
        ? this.hiddenIds.filter((value) => value !== id)
        : [...this.hiddenIds, id];
    },
    tableValue(dataset, i) {
      const value = numberOrNull(dataset.data[i]);
      if (value == null) return "—";
      const n = dataset.sampleCounts?.[i];
      const amount =
        this.mode === "distribution"
          ? `${this.format(value)} % · ${this.format(
              dataset.rawCounts?.[i],
              0
            )} ${this.l("vurderinger", "results")}`
          : this.mode === "failRates"
          ? `${this.format(value * 100)} %`
          : `${this.format(value, 2)} / 5`;
      return `${amount}${n == null ? "" : ` (n=${this.format(n, 0)})`}`;
    },
    tooltipValue(context) {
      if (context.raw && typeof context.raw === "object") {
        const value = numberOrNull(context.raw.y);
        if (value == null) return `${context.dataset.label}: —`;
        return `${context.dataset.label}: ${
          this.mode === "failRates"
            ? `${this.format(value * 100)} %`
            : `${this.format(value, 2)} / 5`
        }${
          context.raw.sampleCount == null
            ? ""
            : ` (n=${this.format(context.raw.sampleCount, 0)})`
        }`;
      }
      return `${context.dataset.label}: ${this.tableValue(
        context.dataset,
        context.dataIndex
      )}`;
    },
  },
};
</script>

<style scoped>
.comparison-panel {
  text-align: left;
  background: #fff;
  border: 1px solid #dce4d4;
  border-radius: 14px;
  padding: 22px;
  min-width: 0;
}
.comparison-heading {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}
.comparison-eyebrow {
  color: #6b805f;
  font-size: 9px;
  letter-spacing: 1.4px;
  font-weight: 600;
}
.comparison-heading h2 {
  font-size: 23px;
  font-weight: 500;
  letter-spacing: -0.8px;
  margin: 0;
}
.comparison-heading p {
  color: #6b7b63;
  font-size: 12px;
  line-height: 1.8;
  max-width: 650px;
}
.comparison-count {
  color: #6b7b63;
  font-size: 10px;
  white-space: nowrap;
  padding-top: 4px;
}
.legend-line {
  width: 24px;
  height: 8px;
  overflow: visible;
  flex-shrink: 0;
}
.comparison-panel :deep(.chart-container) {
  height: clamp(260px, 34vh, 350px);
}
.comparison-footnote {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  font-size: 10px;
  line-height: 1.7;
  color: #728168;
  margin-top: 20px;
}
.comparison-footnote button {
  white-space: nowrap;
  color: #326348;
  font-size: 11px;
}
.comparison-summary-wrap {
  margin: 12px 0 16px;
  border: 1px solid #dfe6d7;
  border-radius: 9px;
  overflow: hidden;
}
.comparison-summary {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.comparison-summary caption {
  text-align: left;
  padding: 10px 14px;
  color: #6a7c61;
  font-size: 11px;
  background: #f5f8ef;
}
.comparison-summary th,
.comparison-summary td {
  text-align: left;
  padding: 12px 10px;
  vertical-align: middle;
  border-top: 1px solid #e4eadb;
  font-variant-numeric: tabular-nums;
}
.comparison-summary thead th {
  color: #6c7f62;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.comparison-course {
  width: 34%;
  min-width: 0;
  font-weight: 400;
}
.comparison-course-label {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.5;
}
.comparison-course strong {
  font-size: 10px;
  letter-spacing: 0.3px;
  color: #6b7e62;
  font-weight: 500;
}
.comparison-course-name {
  display: block;
  font-size: 13px;
  color: #294831;
  margin-top: 3px;
  overflow-wrap: anywhere;
}
.comparison-course .legend-line {
  width: 15px;
}
.summary-value {
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
}
.summary-total {
  display: block;
  font-size: 10px;
  color: #75866a;
  margin-top: 5px;
}
.comparison-remove {
  width: 32px;
  height: 32px;
  font-size: 20px;
  border-radius: 5px;
  color: #6e7f63;
}
.comparison-remove:hover {
  background: #f2eade;
  color: #8d5234;
}
.comparison-summary :deep(.sample-indicator summary) {
  padding: 5px 7px;
  font-size: 11px;
}
.comparison-summary :deep(.sample-indicator p) {
  min-width: 130px;
  max-width: 230px;
}
.comparison-recovery {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}
.comparison-recovery button,
.show-all-courses {
  background: #fff;
  border: 1px solid #cfddc3;
  color: #2b563b;
  font-size: 12px;
  min-height: 40px;
  border-radius: 7px;
  padding: 9px 14px;
}
.show-all-courses {
  margin-top: 15px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 650px) {
  .comparison-summary,
  .comparison-summary tbody {
    display: block;
  }
  .comparison-summary caption {
    display: block;
  }
  .comparison-summary thead {
    display: none;
  }
  .comparison-summary tr {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 10px;
    padding: 14px;
    position: relative;
    border-top: 1px solid #e1e8d9;
  }
  .comparison-summary td,
  .comparison-summary th {
    display: block;
    border: 0;
    padding: 0;
    min-width: 0;
  }
  .comparison-summary .comparison-course {
    width: auto;
    grid-column: 1/4;
    padding-right: 30px;
  }
  .comparison-summary td:nth-child(2) {
    grid-column: 1/4;
    font-size: 11px;
    color: #6d7f63;
  }
  .comparison-summary td:nth-child(2)::before {
    display: inline;
    margin-right: 6px;
  }
  .comparison-summary td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: #6d7f63;
    margin-bottom: 4px;
  }
  .comparison-summary .comparison-remove-cell {
    position: absolute;
    right: 8px;
    top: 12px;
  }
  .comparison-summary .summary-value {
    font-size: 17px;
  }
  .comparison-summary :deep(.sample-indicator summary) {
    font-size: 10px;
    gap: 2px;
    padding: 4px;
  }
  .comparison-summary :deep(.sample-indicator p) {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .comparison-summary .summary-total {
    font-size: 10px;
    line-height: 1.4;
  }
  .comparison-summary .legend-line {
    display: block;
  }
}
.is-hidden {
  opacity: 0.5;
}
.basis-note {
  color: #74826a;
  font-size: 10px;
  line-height: 1.8;
  margin-top: 14px;
}
.comparison-empty {
  padding: 50px 20px;
  text-align: center;
  background: #f6f8f1;
  border-radius: 8px;
  color: #6b7c62;
  font-size: 12px;
  margin: 14px 0;
}
.comparison-empty > span {
  font-size: 30px;
}
.comparison-empty h3 {
  font-size: 19px;
  font-weight: 500;
  color: #3a5835;
  margin: 12px 0;
}
.comparison-empty p {
  max-width: 490px;
  margin: auto;
  line-height: 1.8;
}
.comparison-table-wrap {
  overflow: auto;
  max-height: 350px;
  margin-top: 20px;
  border: 1px solid #dfe7d5;
  border-radius: 7px;
}
.comparison-table-wrap table {
  border-collapse: collapse;
  width: 100%;
  font-size: 11px;
}
.comparison-table-wrap th,
.comparison-table-wrap td {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5ecdd;
  white-space: nowrap;
}
.comparison-table-wrap caption {
  text-align: left;
  padding: 12px;
  font-weight: 500;
}
.comparison-table-wrap thead th {
  background: #f2f6e9;
}
button {
  cursor: pointer;
}
button:focus-visible,
input:focus-visible,
.comparison-table-wrap:focus-visible {
  outline: 3px solid #326348;
  outline-offset: 3px;
}
@media (max-width: 760px) {
  .comparison-panel {
    padding: 19px;
  }
  .comparison-heading {
    flex-wrap: wrap;
    gap: 9px;
  }
  .comparison-heading h2 {
    font-size: 22px;
  }
  .comparison-panel :deep(.chart-container) {
    height: 310px;
  }
  .comparison-footnote {
    flex-wrap: wrap;
    gap: 10px;
  }
}
.legend-small {
  display: inline-block;
  margin-left: 7px;
  font-size: 9px;
  color: #835b21;
  background: #fff0d0;
  border: 1px solid #ebd7a6;
  border-radius: 4px;
  padding: 2px 5px;
  white-space: nowrap;
}

@media (max-width: 650px) {
  .comparison-summary tr {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 11px 12px;
    padding: 13px 11px;
  }
  .comparison-summary .comparison-course,
  .comparison-summary td:nth-child(2) {
    grid-column: 1 / -1;
  }
  .shared-period .comparison-summary td:nth-child(2) {
    display: none;
  }
  .comparison-summary td:nth-child(5) {
    grid-column: auto;
    display: block;
  }
  .comparison-summary td:nth-child(5):has(details[open]) {
    grid-column: 1 / -1;
  }
  .comparison-summary td:nth-child(5)::before {
    width: 100%;
    margin: 0;
  }
  .comparison-summary .summary-total {
    margin: 0;
    font-size: 10px;
  }
  .comparison-course-name {
    font-size: 12px;
  }
  .comparison-summary :deep(.sample-indicator summary) {
    padding: 4px;
    gap: 6px;
  }
}
@media (max-width: 420px) {
  .comparison-panel {
    padding: 13px;
  }
  .comparison-heading p {
    font-size: 11px;
    line-height: 1.65;
  }
  .comparison-heading h2 {
    font-size: 21px;
  }
}

.comparison-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin: 12px 0 8px;
}
.comparison-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 44px;
  padding: 4px 8px;
  border: 1px solid #dfe6d7;
  border-radius: 7px;
  font-size: 11px;
  color: #294831;
  cursor: pointer;
}
.comparison-legend-item input {
  width: 16px;
  height: 16px;
  accent-color: #326348;
  flex-shrink: 0;
}
.comparison-legend-item .legend-line {
  width: 20px;
}
.sample-alert {
  margin: 0 0 10px;
  padding: 8px 10px;
  color: #805b21;
  background: #fff7e5;
  border-radius: 6px;
}
@media (max-width: 420px) {
  .comparison-legend {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }
  .comparison-legend-item {
    gap: 5px;
    padding: 4px 6px;
    font-size: 10px;
    min-width: 0;
  }
  .comparison-legend-item span {
    overflow-wrap: anywhere;
  }
  .comparison-legend-item .legend-line {
    width: 14px;
  }
}
</style>
